import { useEffect, useMemo, useState, useCallback } from 'react';
import { collection, onSnapshot, query, orderBy, addDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { formatCurrency } from '@/lib/utils';
import {
  addIncome,
  addExpense,
  markShoppingItemAsBought,
  addSavingsTransaction,
} from '@/services/financeService';
import {
  incomePath,
  expensesPath,
  shoppingListsPath,
  shoppingListItemsPath,
  savingsGoalsPath,
  budgetsPath,
} from '@/config/firestoreCollections';
import { createShoppingItem, createSavingsGoal } from '@/models/financeModels';
import toast from 'react-hot-toast';
import Tooltip from '@/components/common/Tooltip';
import TransactionInsightsPanel from '@/components/transactions/TransactionInsightsPanel';

const pad2 = (n) => String(n).padStart(2, '0');
const deriveMonthlyPeriodId = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

const ITEM_STATUS_TOOLTIPS = {
  pending: 'Not bought yet.',
  purchased: 'Already bought and counted in your spending.',
  refunded: 'Refunded – this amount is excluded from your spending.'
};

const FinanceOverviewPage = () => {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  const [incomeForm, setIncomeForm] = useState({ source: '', amount: '', currency: 'USD' });
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', category: 'other', currency: 'USD' });
  const [shoppingItemForm, setShoppingItemForm] = useState({ listId: '', name: '', quantity: 1, unit: 'unit', plannedUnitPrice: '' });
  const [savingsGoalForm, setSavingsGoalForm] = useState({ name: '', targetAmount: '', currency: 'USD', deadline: '' });
  const [savingsTxForm, setSavingsTxForm] = useState({ goalId: '', amount: '', type: 'deposit' });
  const [shoppingLists, setShoppingLists] = useState([]);
  const [shoppingItems, setShoppingItems] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [goals, setGoals] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [markModal, setMarkModal] = useState({ open: false, item: null });
  const [insightsRefreshKey, setInsightsRefreshKey] = useState(0);

  // Callback to refresh insights panel when data changes
  const handleInsightsRefresh = useCallback(() => {
    setInsightsRefreshKey((prev) => prev + 1);
  }, []);

  const periodId = useMemo(() => deriveMonthlyPeriodId(new Date()), []);

  useEffect(() => {
    if (!userId) return;

    const unsubIncome = onSnapshot(
      query(collection(db, incomePath(userId)), orderBy('createdAt', 'desc')), 
      (snap) => {
        setIncomes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.warn('Income snapshot error (non-critical):', error.message);
        setIncomes([]);
      }
    );

    const unsubExpense = onSnapshot(
      query(collection(db, expensesPath(userId)), orderBy('createdAt', 'desc')), 
      (snap) => {
        setExpenses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.warn('Expense snapshot error (non-critical):', error.message);
        setExpenses([]);
      }
    );

    const unsubLists = onSnapshot(
      collection(db, shoppingListsPath(userId)), 
      (snap) => {
        setShoppingLists(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.warn('Shopping lists snapshot error (non-critical):', error.message);
        setShoppingLists([]);
      }
    );

    // Only subscribe to shopping items if a valid list is selected
    let unsubItems = () => {};
    if (shoppingItemForm.listId && shoppingItemForm.listId !== 'default') {
      unsubItems = onSnapshot(
        collection(db, shoppingListItemsPath(userId, shoppingItemForm.listId)), 
        (snap) => {
          setShoppingItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        },
        (error) => {
          console.warn('Shopping items snapshot error (non-critical):', error.message);
          setShoppingItems([]);
        }
      );
    } else {
      // No list selected, clear items
      setShoppingItems([]);
    }

    const unsubGoals = onSnapshot(
      collection(db, savingsGoalsPath(userId)), 
      (snap) => {
        setGoals(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.warn('Goals snapshot error (non-critical):', error.message);
        setGoals([]);
      }
    );

    const unsubBudgets = onSnapshot(
      collection(db, budgetsPath(userId)), 
      (snap) => {
        setBudgets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.warn('Budgets snapshot error (non-critical):', error.message);
        setBudgets([]);
      }
    );

    return () => {
      unsubIncome();
      unsubExpense();
      unsubLists();
      unsubItems();
      unsubGoals();
      unsubBudgets();
    };
  }, [userId, shoppingItemForm.listId]);

  if (!userId) {
    return <div className="p-4">Please sign in to view and manage your finances.</div>;
  }

  const currentBudget = budgets.find((b) => b.id === periodId) || {};

  const handleAddIncome = async (e) => {
    e.preventDefault();
    try {
      await addIncome({ userId, ...incomeForm, amount: Number(incomeForm.amount) });
      toast.success('Income recorded.');
      setIncomeForm({ source: '', amount: '', currency: incomeForm.currency });
      handleInsightsRefresh(); // Refresh insights panel
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await addExpense({ userId, ...expenseForm, amount: Number(expenseForm.amount) });
      toast.success('Expense recorded.');
      setExpenseForm({ description: '', amount: '', category: 'other', currency: expenseForm.currency });
      handleInsightsRefresh(); // Refresh insights panel
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddShoppingItem = async (e) => {
    e.preventDefault();
    if (!shoppingItemForm.listId) {
      toast.error('Please choose or create a shopping list ID.');
      return;
    }
    try {
      const item = createShoppingItem(userId, shoppingItemForm.listId, {
        name: shoppingItemForm.name,
        quantity: Number(shoppingItemForm.quantity),
        unit: shoppingItemForm.unit,
        plannedUnitPrice: shoppingItemForm.plannedUnitPrice ? Number(shoppingItemForm.plannedUnitPrice) : undefined,
        category: 'groceries',
      });
      await addDoc(collection(db, shoppingListItemsPath(userId, shoppingItemForm.listId)), item);
      toast.success('Shopping item added.');
      setShoppingItemForm({ ...shoppingItemForm, name: '', quantity: 1, plannedUnitPrice: '' });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddSavingsGoal = async (e) => {
    e.preventDefault();
    try {
      const goal = createSavingsGoal(userId, {
        name: savingsGoalForm.name,
        targetAmount: Number(savingsGoalForm.targetAmount),
        currency: savingsGoalForm.currency,
        deadline: savingsGoalForm.deadline || undefined,
      });
      await addDoc(collection(db, savingsGoalsPath(userId)), goal);
      toast.success('Savings goal created.');
      setSavingsGoalForm({ name: '', targetAmount: '', currency: savingsGoalForm.currency, deadline: '' });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddSavingsTransaction = async (e) => {
    e.preventDefault();
    try {
      await addSavingsTransaction({
        userId,
        goalId: savingsTxForm.goalId,
        amount: Number(savingsTxForm.amount),
        type: savingsTxForm.type,
      });
      toast.success('Savings updated.');
      setSavingsTxForm({ goalId: savingsTxForm.goalId, amount: '', type: savingsTxForm.type });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleMarkBought = async () => {
    const { item, pricePerUnit, quantity } = markModal;
    if (!item) return;
    try {
      await markShoppingItemAsBought({
        userId,
        listId: item.listId,
        itemId: item.id,
        pricePerUnit: Number(pricePerUnit),
        quantity: Number(quantity),
      });
      toast.success('Marked as bought and recorded expense.');
      setMarkModal({ open: false, item: null });
      handleInsightsRefresh(); // Refresh insights panel
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openMarkModal = (item) => {
    setMarkModal({ open: true, item, pricePerUnit: item.plannedUnitPrice || '', quantity: item.quantity || 1 });
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-8">
      <h1 className="text-3xl font-bold">FinList Overview</h1>

      {/* Smart Transaction Insights Panel */}
      <TransactionInsightsPanel key={insightsRefreshKey} onRefresh={handleInsightsRefresh} />

      {/* Income */}
      <section className="border rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/30">
        <h2 className="text-xl font-semibold mb-3">Record Income</h2>
        <form className="flex flex-wrap gap-3" onSubmit={handleAddIncome}>
          <Input placeholder="Source" value={incomeForm.source} onChange={(e) => setIncomeForm({ ...incomeForm, source: e.target.value })} required />
          <Input type="number" min="0" placeholder="Amount" value={incomeForm.amount} onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })} required />
          <Input placeholder="Currency" value={incomeForm.currency} onChange={(e) => setIncomeForm({ ...incomeForm, currency: e.target.value })} />
          <Tooltip label="Record income so your dashboard can show accurate balances.">
            <Button type="submit">Add Income</Button>
          </Tooltip>
        </form>
        <div className="mt-3 text-sm text-gray-600">Income this month: {formatCurrency(currentBudget.actualIncome || 0)}</div>
      </section>

      {/* Expense */}
      <section className="border rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/30">
        <h2 className="text-xl font-semibold mb-3">Record Expense</h2>
        <form className="flex flex-wrap gap-3" onSubmit={handleAddExpense}>
          <Input placeholder="Description" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} required />
          <Input type="number" min="0" placeholder="Amount" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} required />
          <Input placeholder="Category" value={expenseForm.category} onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })} />
          <Tooltip label="Log a new expense and link it to your lists and category.">
            <Button type="submit">Add Expense</Button>
          </Tooltip>
        </form>
        <div className="mt-3 text-sm text-gray-600">Total expenses this month: {formatCurrency(currentBudget.actualExpenses || 0)}</div>
      </section>

      {/* Shopping list */}
      <section className="border rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/30">
        <h2 className="text-xl font-semibold mb-3">Shopping List</h2>
        <form className="flex flex-wrap gap-3" onSubmit={handleAddShoppingItem}>
          <Input placeholder="List ID (e.g. weekly-groceries)" value={shoppingItemForm.listId} onChange={(e) => setShoppingItemForm({ ...shoppingItemForm, listId: e.target.value })} required />
          <Input placeholder="Item" value={shoppingItemForm.name} onChange={(e) => setShoppingItemForm({ ...shoppingItemForm, name: e.target.value })} required />
          <Input type="number" min="1" placeholder="Qty" value={shoppingItemForm.quantity} onChange={(e) => setShoppingItemForm({ ...shoppingItemForm, quantity: e.target.value })} />
          <Input placeholder="Unit" value={shoppingItemForm.unit} onChange={(e) => setShoppingItemForm({ ...shoppingItemForm, unit: e.target.value })} />
          <Input type="number" min="0" step="0.01" placeholder="Planned unit price" value={shoppingItemForm.plannedUnitPrice} onChange={(e) => setShoppingItemForm({ ...shoppingItemForm, plannedUnitPrice: e.target.value })} />
          <Button type="submit">Add Item</Button>
        </form>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2">Item</th>
                <th>Qty</th>
                <th>Planned Unit</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shoppingItems.map((item) => {
                const statusLabel = (item.status || 'pending').toLowerCase();
                return (
                  <tr key={item.id} className="border-b">
                    <td className="py-2">{item.name}</td>
                    <td>x{item.quantity}</td>
                    <td>{item.plannedUnitPrice ? formatCurrency(item.plannedUnitPrice) : '-'}</td>
                    <td className="capitalize">
                      <Tooltip label={ITEM_STATUS_TOOLTIPS[statusLabel] || 'Status update for this item.'}>
                        <span>{statusLabel}</span>
                      </Tooltip>
                    </td>
                    <td>
                      {statusLabel === 'pending' && (
                        <Tooltip label="Mark this item as bought and record its price and quantity.">
                          <Button size="sm" variant="secondary" onClick={() => openMarkModal(item)}>
                            Bought
                          </Button>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Savings goals */}
      <section className="border rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/30">
        <h2 className="text-xl font-semibold mb-3">Savings Goals</h2>
        <form className="flex flex-wrap gap-3" onSubmit={handleAddSavingsGoal}>
          <Input placeholder="Goal name" value={savingsGoalForm.name} onChange={(e) => setSavingsGoalForm({ ...savingsGoalForm, name: e.target.value })} required />
          <Input type="number" min="0" placeholder="Target amount" value={savingsGoalForm.targetAmount} onChange={(e) => setSavingsGoalForm({ ...savingsGoalForm, targetAmount: e.target.value })} required />
          <Input placeholder="Currency" value={savingsGoalForm.currency} onChange={(e) => setSavingsGoalForm({ ...savingsGoalForm, currency: e.target.value })} />
          <Input type="date" value={savingsGoalForm.deadline} onChange={(e) => setSavingsGoalForm({ ...savingsGoalForm, deadline: e.target.value })} />
          <Button type="submit">Create Goal</Button>
        </form>

        <div className="mt-4 space-y-2">
          {goals.map((g) => {
            const progress = g.targetAmount ? Math.min(100, Math.round((g.currentSaved || 0) / g.targetAmount * 100)) : 0;
            return (
              <div key={g.id} className="p-3 border rounded">
                <div className="font-semibold">{g.name}</div>
                <div className="text-sm text-gray-600">{formatCurrency(g.currentSaved || 0)} / {formatCurrency(g.targetAmount || 0)} ({progress}%)</div>
                <div className="mt-2 h-2 bg-gray-200 rounded">
                  <div className="h-2 bg-green-500 rounded" style={{ width: `${progress}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4">
          <h3 className="font-semibold mb-2">Update Savings</h3>
          <form className="flex flex-wrap gap-3" onSubmit={handleAddSavingsTransaction}>
            <select className="border rounded px-2 py-2" value={savingsTxForm.goalId} onChange={(e) => setSavingsTxForm({ ...savingsTxForm, goalId: e.target.value })} required>
              <option value="">Select goal</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <Input type="number" min="0" placeholder="Amount" value={savingsTxForm.amount} onChange={(e) => setSavingsTxForm({ ...savingsTxForm, amount: e.target.value })} required />
            <select className="border rounded px-2 py-2" value={savingsTxForm.type} onChange={(e) => setSavingsTxForm({ ...savingsTxForm, type: e.target.value })}>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
            </select>
            <Button type="submit">Save</Button>
          </form>
        </div>
      </section>

      {/* Budget summary */}
      <section className="border rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/30">
        <h2 className="text-xl font-semibold mb-3">Budget Summary (This Month)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="p-3 border rounded">
            <div className="text-gray-500">Planned Income</div>
            <div className="font-semibold">{formatCurrency(currentBudget.plannedIncome || 0)}</div>
          </div>
          <div className="p-3 border rounded">
            <div className="text-gray-500">Actual Income</div>
            <div className="font-semibold">{formatCurrency(currentBudget.actualIncome || 0)}</div>
          </div>
          <div className="p-3 border rounded">
            <div className="text-gray-500">Planned Expenses</div>
            <div className="font-semibold">{formatCurrency(currentBudget.plannedExpenses || 0)}</div>
          </div>
          <div className="p-3 border rounded">
            <div className="text-gray-500">Actual Expenses</div>
            <div className="font-semibold text-red-600">{formatCurrency(currentBudget.actualExpenses || 0)}</div>
          </div>
          <div className="p-3 border rounded">
            <div className="text-gray-500">Planned Savings</div>
            <div className="font-semibold">{formatCurrency(currentBudget.plannedSavings || 0)}</div>
          </div>
          <div className="p-3 border rounded">
            <div className="text-gray-500">Actual Savings</div>
            <div className="font-semibold text-green-600">{formatCurrency(currentBudget.actualSavings || 0)}</div>
          </div>
        </div>
      </section>

      {/* Modal for purchased item */}
      <Modal isOpen={markModal.open} onClose={() => setMarkModal({ open: false, item: null })} title="Mark as bought">
        <div className="space-y-3">
          <div className="text-sm">Tell us the price so we can record the expense.</div>
          <Input type="number" min="0" step="0.01" placeholder="Price per unit" value={markModal.pricePerUnit ?? ''} onChange={(e) => setMarkModal({ ...markModal, pricePerUnit: e.target.value })} />
          <Input type="number" min="1" placeholder="Quantity" value={markModal.quantity ?? ''} onChange={(e) => setMarkModal({ ...markModal, quantity: e.target.value })} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMarkModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleMarkBought}>Confirm</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FinanceOverviewPage;
